import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, RotateCcw, Code, FileText, Download, Upload } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

export const HtmlEncoderTool: React.FC = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // HTML entity mapping
  const htmlEntities: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;',
    ' ': '&nbsp;',
    '¡': '&iexcl;',
    '¢': '&cent;',
    '£': '&pound;',
    '¤': '&curren;',
    '¥': '&yen;',
    '¦': '&brvbar;',
    '§': '&sect;',
    '¨': '&uml;',
    '©': '&copy;',
    'ª': '&ordf;',
    '«': '&laquo;',
    '¬': '&not;',
    '®': '&reg;',
    '¯': '&macr;',
    '°': '&deg;',
    '±': '&plusmn;',
    '²': '&sup2;',
    '³': '&sup3;',
    '´': '&acute;',
    'µ': '&micro;',
    '¶': '&para;',
    '·': '&middot;',
    '¸': '&cedil;',
    '¹': '&sup1;',
    'º': '&ordm;',
    '»': '&raquo;',
    '¼': '&frac14;',
    '½': '&frac12;',
    '¾': '&frac34;',
    '¿': '&iquest;'
  };

  const encodeHtml = (text: string, encodeSpaces: boolean = false) => {
    let encoded = text;
    
    // Basic HTML entities
    encoded = encoded
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    // Optional space encoding
    if (encodeSpaces) {
      encoded = encoded.replace(/ /g, '&nbsp;');
    }

    setOutput(encoded);
    return encoded;
  };

  const decodeHtml = (text: string) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      const decoded = textarea.value;
      setOutput(decoded);
      return decoded;
    } catch (err) {
      setOutput(t('tools.htmlEncoder.invalidHtml'));
      return null;
    }
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    
    if (value.trim()) {
      if (mode === 'encode') {
        encodeHtml(value);
      } else {
        decodeHtml(value);
      }
    } else {
      setOutput('');
    }
  };

  const switchMode = () => {
    const newMode = mode === 'encode' ? 'decode' : 'encode';
    setMode(newMode);
    
    // Swap input and output
    const tempInput = input;
    setInput(output);
    setOutput(tempInput);
    
    // Process the swapped input
    if (output.trim()) {
      if (newMode === 'encode') {
        encodeHtml(output);
      } else {
        decodeHtml(output);
      }
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const downloadResult = () => {
    if (output) {
      const filename = mode === 'encode' ? t('tools.htmlEncoder.encodedFilename') : t('tools.htmlEncoder.decodedFilename');
      const blob = new Blob([output], { type: 'text/html' });
      saveAs(blob, filename);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInput(content);
        if (mode === 'encode') {
          encodeHtml(content);
        } else {
          decodeHtml(content);
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
  };

  const commonExamples = [
    { original: '<div class="example">Hello & Welcome</div>', encoded: '&lt;div class=&quot;example&quot;&gt;Hello &amp; Welcome&lt;/div&gt;' },
    { original: 'Price: $100 < $200', encoded: 'Price: $100 &lt; $200' },
    { original: 'Quote: "Hello World"', encoded: 'Quote: &quot;Hello World&quot;' },
    { original: "It's a test", encoded: 'It&#39;s a test' }
  ];

  const useExample = (example: { original: string; encoded: string }) => {
    if (mode === 'encode') {
      setInput(example.original);
      encodeHtml(example.original);
    } else {
      setInput(example.encoded);
      decodeHtml(example.encoded);
    }
  };

  return (
    <div className="space-y-6">
      {/* Mode Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Code className="w-5 h-5" />
            <span>{t('tools.htmlEncoder.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={() => {
                setMode('encode');
                if (input) encodeHtml(input);
              }}
              variant={mode === 'encode' ? 'primary' : 'secondary'}
            >
              <Code className="w-4 h-4 mr-2" />
              {t('tools.htmlEncoder.encode')}
            </Button>
            <Button
              onClick={() => {
                setMode('decode');
                if (input) decodeHtml(input);
              }}
              variant={mode === 'decode' ? 'primary' : 'secondary'}
            >
              <FileText className="w-4 h-4 mr-2" />
              {t('tools.htmlEncoder.decode')}
            </Button>
            <Button
              onClick={switchMode}
              variant="outline"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {t('tools.htmlEncoder.switch')}
            </Button>
            <Button
              onClick={() => output && copyToClipboard(output)}
              disabled={!output}
              variant="outline"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('common.copy')}
            </Button>
            <Button
              onClick={downloadResult}
              disabled={!output}
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('common.download')}
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
            >
              {t('common.clear')}
            </Button>
          </div>

          <div className="mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".html,.htm,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="mb-2"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('common.selectFile')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Input/Output */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>
              {mode === 'encode' ? t('tools.htmlEncoder.htmlInput') : t('tools.htmlEncoder.encodedHtmlInput')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={
                mode === 'encode'
                  ? t('tools.htmlEncoder.enterHtmlToEncode')
                  : t('tools.htmlEncoder.enterHtmlToDecode')
              }
              className="min-h-[300px] font-mono text-sm"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                {mode === 'encode' ? t('tools.htmlEncoder.encodedOutput') : t('tools.htmlEncoder.decodedOutput')}
              </span>
              {output && (
                <span className="text-sm text-gray-500">
                  {output.length} {t('tools.htmlEncoder.chars')}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder={
                mode === 'encode'
                  ? t('tools.htmlEncoder.encodedOutputPlaceholder')
                  : t('tools.htmlEncoder.decodedOutputPlaceholder')
              }
              className="min-h-[300px] font-mono text-sm bg-gray-50 dark:bg-gray-800"
            />
          </CardContent>
        </Card>
      </div>

      {/* Examples */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.htmlEncoder.commonExamples')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {commonExamples.map((example, index) => (
              <div key={index} className="p-3 border rounded-lg dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 font-medium">{t('tools.htmlEncoder.original')}:</span>
                    <div className="font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded mt-1">
                      {example.original}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 font-medium">{t('tools.htmlEncoder.encoded')}:</span>
                    <div className="font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded mt-1">
                      {example.encoded}
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => useExample(example)}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                >
                  {t('tools.htmlEncoder.useExample')}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
